/**
 * Push Notification Queue Modal
 *
 * Read view is a full diagnostic of one push: who it went to, which app/device/platform it was
 * addressed to, the delivery timeline, and — when it failed — exactly why.
 */

import { useEffect } from 'react';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconDeviceMobile, IconMail, IconPhone, IconUser } from '@tabler/icons-react';
import {
  Stack,
  Group,
  Button,
  Alert,
  Text,
  TextInput,
  Badge,
  Textarea,
  Modal,
  Paper,
  Avatar,
  Divider,
  Code,
  SimpleGrid,
} from '../../../../../components/ui';
import { StatusTracker } from '../../../../../components/advanced/workflow';
import type { PushQueueModalProps } from '../types';
import { decodePushBody, statusOf, recipientName, parseAuditStamp } from '../types';
import { getStatusColor, formatDateTime } from '../../types';

/** Renders an ISO timestamp from the API; returns undefined so callers can skip empty steps. */
function toDate(value?: string | null): Date | undefined {
  if (!value) return undefined;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

/** A labelled value; renders nothing when there's no value, so the layout never shows empty rows. */
function Field({ label, value }: { label: string; value?: string | number | null }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text size="sm" style={{ wordBreak: 'break-word' }}>{value}</Text>
    </div>
  );
}

export function PushQueueModal({ opened, onClose, item, mode, onSave, loading }: PushQueueModalProps) {
  const isEditMode = mode === 'edit';
  const form = useForm({ initialValues: { title: '', body: '' } });

  useEffect(() => {
    if (item) {
      form.setValues({ title: item.title || '', body: decodePushBody(item.body) });
    }
  }, [item]);

  const handleSubmit = async (values: typeof form.values) => {
    if (onSave && item) {
      await onSave({ ...item, ...values } as any);
      onClose();
    }
  };

  if (!item) return null;

  const status = statusOf(item);
  const failed = status === 'Failed';

  // QueuedDateTime is null on rows created before the worker recorded it, so fall back to the row's
  // creation stamp — the moment it was queued is exactly when it was written.
  const queuedAt = toDate(item.queuedAt) ?? parseAuditStamp(item.createdDateTime);
  const processingAt = toDate(item.processingStartedAt);
  const sentAt = toDate(item.deliveredAt) ?? toDate(item.completedAt) ?? toDate(item.sentAt);

  // The worker only writes CompletedDateTime on success, so a failed row has no "finished" stamp.
  // modifiedDateTime is written in the same update that marks it Failed, which is precisely when the
  // failure happened.
  const failedAt = toDate(item.completedAt) ?? parseAuditStamp(item.modifiedDateTime);

  // The terminal step is Sent OR Failed — never both. Previously the timeline only had queued/sent,
  // so a failed push still rendered as "Queued → Sent".
  const statusSteps = [
    { key: 'queued', label: 'Queued', timestamp: queuedAt },
    { key: 'processing', label: 'Processing', timestamp: processingAt },
    failed
      ? { key: 'failed', label: 'Failed', timestamp: failedAt }
      : { key: 'sent', label: 'Sent', timestamp: sentAt },
  ];

  const completedSteps = [
    queuedAt || item.createdDateTime ? 'queued' : null,
    processingAt ? 'processing' : null,
    !failed && item.isSent ? 'sent' : null,
  ].filter(Boolean) as string[];

  const name = recipientName(item);
  const hasRecipient = !!(name || item.userEmail || item.userCellphone || item.userId);
  const token = item.deviceToken || '';

  return (
    <Modal
      opened={opened}
      onClose={() => { form.reset(); onClose(); }}
      title={isEditMode ? 'Edit Push' : `Push Details — #${item.messageID}`}
      size="lg"
    >
      <Stack gap="md">
        <Group gap="xs">
          {/* Semantic status names go on gradientVariant, not `color` (Mantine can't resolve them). */}
          <Badge gradientVariant={getStatusColor(status as any)} size="lg">{status}</Badge>
          {item.retriesExhausted && failed && (
            <Badge variant="light" color="gray" size="lg">No auto-retry</Badge>
          )}
          {item.platform && <Badge variant="light" color="blue" size="lg">{item.platform}</Badge>}
          {item.appId && <Badge variant="light" color="grape" size="lg">{item.appId}</Badge>}
        </Group>

        <Paper p="md" withBorder>
          {/*
            "stepper", not "timeline": StatusTracker's timeline variant renders a hardcoded vertical
            stack and ignores `orientation` — only the stepper honours it.
            The track scrolls within itself so extra steps never make the modal scroll sideways.
          */}
          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: 'min-content' }}>
              <StatusTracker
                title="Delivery Status"
                variant="stepper"
                steps={statusSteps}
                currentStatus={status.toLowerCase()}
                completedSteps={completedSteps}
                failedStep={failed ? 'failed' : undefined}
                showTimestamps
                orientation="horizontal"
              />
            </div>
          </div>
        </Paper>

        {failed && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} title={item.errorCode || 'Delivery failed'}>
            <Stack gap={4}>
              <Text size="sm">{item.failureReason || item.errorMessage || 'The provider rejected this push.'}</Text>
              <Text size="xs" c="dimmed">
                Attempt {item.retryCount ?? 0} of {item.maxRetryCount ?? 0}
                {item.retriesExhausted
                  ? ' — the worker will not try again on its own. Use Retry to requeue it.'
                  : item.nextRetryAt
                    ? ` — next attempt ${new Date(item.nextRetryAt).toLocaleString()}.`
                    : ''}
              </Text>
            </Stack>
          </Alert>
        )}

        {isEditMode ? (
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              <TextInput label="Title" required {...form.getInputProps('title')} />
              <Textarea label="Message" required rows={4} {...form.getInputProps('body')} />
              <Group justify="flex-end" gap="xs">
                <Button variant="subtle" onClick={onClose}>Cancel</Button>
                <Button type="submit" loading={loading} variant="light">Save</Button>
              </Group>
            </Stack>
          </form>
        ) : (
          <Stack gap="md">
            <div>
              <Text fw={600} size="sm" mb="xs">Recipient</Text>
              <Paper p="md" withBorder>
                {hasRecipient ? (
                  <Group gap="md" wrap="nowrap" align="center">
                    <Avatar src={item.userAvatar || undefined} radius="xl" size="lg">
                      {name ? name.charAt(0).toUpperCase() : <IconUser size={20} />}
                    </Avatar>

                    {/* Details span the rest of the card rather than stacking in a narrow column. */}
                    <SimpleGrid
                      cols={{ base: 1, sm: 3 }}
                      spacing="md"
                      verticalSpacing="xs"
                      style={{ flex: 1, minWidth: 0 }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <Text size="xs" c="dimmed">Name</Text>
                        <Text size="sm" fw={600} lineClamp={1}>{name || 'Unknown user'}</Text>
                      </div>

                      <div style={{ minWidth: 0 }}>
                        <Text size="xs" c="dimmed">Email</Text>
                        {item.userEmail ? (
                          <Group gap={6} wrap="nowrap">
                            <IconMail size={13} style={{ flexShrink: 0 }} />
                            <Text size="sm" style={{ wordBreak: 'break-all' }}>{item.userEmail}</Text>
                          </Group>
                        ) : (
                          <Text size="sm" c="dimmed">—</Text>
                        )}
                      </div>

                      <div style={{ minWidth: 0 }}>
                        <Text size="xs" c="dimmed">Cellphone</Text>
                        {item.userCellphone ? (
                          <Group gap={6} wrap="nowrap">
                            <IconPhone size={13} style={{ flexShrink: 0 }} />
                            <Text size="sm">{item.userCellphone}</Text>
                          </Group>
                        ) : (
                          <Text size="sm" c="dimmed">—</Text>
                        )}
                      </div>

                      {item.userId && (
                        <div style={{ gridColumn: '1 / -1', minWidth: 0 }}>
                          <Text size="xs" c="dimmed">User ID</Text>
                          <Text size="xs" c="dimmed" style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                            {item.userId}
                          </Text>
                        </div>
                      )}
                    </SimpleGrid>
                  </Group>
                ) : (
                  <Group gap="xs" wrap="nowrap">
                    <IconDeviceMobile size={16} />
                    <Text size="sm" c="dimmed">
                      No registered owner for this device — it was most likely unregistered or the app uninstalled.
                    </Text>
                  </Group>
                )}
              </Paper>
            </div>

            <div>
              <Text fw={600} size="sm" mb="xs">Device</Text>
              <Paper p="md" withBorder>
                <SimpleGrid cols={2} spacing="sm">
                  <Field label="App" value={item.appId} />
                  <Field label="Platform" value={item.platform} />
                  <Field label="Device name" value={item.deviceName} />
                  <Field label="Device ID" value={item.deviceId} />
                </SimpleGrid>
                {token && (
                  <>
                    <Divider my="sm" />
                    <Text size="xs" c="dimmed">Push token</Text>
                    <Code block style={{ fontSize: 11, wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
                      {token.length > 96 ? `${token.slice(0, 96)}…` : token}
                    </Code>
                  </>
                )}
              </Paper>
            </div>

            <div>
              <Text fw={600} size="sm" mb="xs">Content</Text>
              <Paper p="md" withBorder>
                <Stack gap="xs">
                  <Field label="Title" value={item.title || '(No title)'} />
                  <div>
                    <Text size="xs" c="dimmed">Message</Text>
                    <Text size="sm">{decodePushBody(item.body) || '(No message)'}</Text>
                  </div>
                  {item.imageUrl && <Field label="Image URL" value={item.imageUrl} />}
                </Stack>
              </Paper>
            </div>

            <div>
              <Text fw={600} size="sm" mb="xs">Delivery</Text>
              <Paper p="md" withBorder>
                <SimpleGrid cols={2} spacing="sm">
                  <Field label="Priority" value={item.priority} />
                  <Field label="Template" value={item.templateName || (item.templateID ? `#${item.templateID}` : null)} />
                  <Field label="Queued" value={formatDateTime(item.createdDateTime)} />
                  <Field label="Scheduled for" value={item.scheduledFor ? new Date(item.scheduledFor).toLocaleString() : null} />
                  <Field label="Sent" value={sentAt ? sentAt.toLocaleString() : null} />
                  <Field label="Provider message ID" value={item.correlationId} />
                </SimpleGrid>
              </Paper>
            </div>

            {item.data && (
              <div>
                <Text fw={600} size="sm" mb="xs">Payload</Text>
                <Code block style={{ fontSize: 11, maxHeight: 160, overflow: 'auto' }}>
                  {(() => {
                    try {
                      return JSON.stringify(JSON.parse(item.data), null, 2);
                    } catch {
                      return item.data;
                    }
                  })()}
                </Code>
              </div>
            )}

            <Group justify="flex-end"><Button variant="subtle" onClick={onClose}>Close</Button></Group>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
